import { validateStoryTrailConfig, type StoryTrailConfig } from './StoryTrailConfig.js';

export type StoryTrailCheckResult = 'incomplete' | 'incorrect' | 'correct';

export class StoryTrailState {
  private readonly collectedIds = new Set<string>();
  private readonly orderedIds: Array<string | null>;

  constructor(readonly config: StoryTrailConfig) {
    validateStoryTrailConfig(config);
    this.orderedIds = Array.from({ length: config.events.length }, () => null);
  }

  get collectedEventIds() { return [...this.collectedIds]; }
  get placedOrder() { return [...this.orderedIds]; }
  get allEventsCollected() { return this.collectedIds.size === this.config.events.length; }
  get isComplete() { return this.check() === 'correct'; }

  collect(eventId: string) {
    if (!this.hasEvent(eventId) || this.collectedIds.has(eventId)) return false;
    this.collectedIds.add(eventId);
    return true;
  }

  collectAll() {
    this.config.events.forEach(event => this.collectedIds.add(event.id));
  }

  place(eventId: string, slotIndex: number) {
    if (!this.collectedIds.has(eventId) || !this.isValidSlot(slotIndex)) return false;
    const priorSlot = this.orderedIds.indexOf(eventId);
    if (priorSlot >= 0) this.orderedIds[priorSlot] = null;

    const displaced = this.orderedIds[slotIndex];
    this.orderedIds[slotIndex] = eventId;
    if (displaced && priorSlot >= 0 && priorSlot !== slotIndex) {
      this.orderedIds[priorSlot] = displaced;
    }
    return true;
  }

  remove(slotIndex: number) {
    if (!this.isValidSlot(slotIndex) || this.orderedIds[slotIndex] === null) return false;
    this.orderedIds[slotIndex] = null;
    return true;
  }

  resetOrder() {
    const changed = this.orderedIds.some(Boolean);
    this.orderedIds.fill(null);
    return changed;
  }

  check(): StoryTrailCheckResult {
    if (this.orderedIds.some(id => id === null)) return 'incomplete';
    return this.orderedIds.every((id, index) => id === this.config.correctOrder[index])
      ? 'correct'
      : 'incorrect';
  }

  firstIncorrectSlot() {
    return this.orderedIds.findIndex((id, index) => id !== this.config.correctOrder[index]);
  }

  eventForId(eventId: string | null) {
    return eventId ? this.config.events.find(event => event.id === eventId) : undefined;
  }

  private hasEvent(eventId: string) {
    return this.config.events.some(event => event.id === eventId);
  }

  private isValidSlot(slotIndex: number) {
    return Number.isInteger(slotIndex) && slotIndex >= 0 && slotIndex < this.orderedIds.length;
  }
}
